//import { getMessage, getData, isEncrypted } from "../utils/responseDecoder";
import { getMessage } from "../utils/responseDecoder";

import {
  API_BASE_URL,
  HRMS_BASE_URL,
  ESS_BASE_URL,
  PSM_BASE_URL,
  CRM_BASE_URL,
  FSM_BASE_URL,
  IMS_BASE_URL,
} from "./config";
import sessionDB from "../utils/sessionDB";
import authApi from "../utils/api";
const NORMALIZED_API_BASE_URL = (API_BASE_URL || "").replace(/\/+$/, "");

export const ORG_API_BASE_URL = NORMALIZED_API_BASE_URL;

export function resolveApiUrl(path = "") {
  if (!path) return NORMALIZED_API_BASE_URL || "";
  if (/^https?:\/\//i.test(path)) return path;
  if (!NORMALIZED_API_BASE_URL) return path;
  return `${NORMALIZED_API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function resolveOrgApiUrl(path = "") {
  return resolveApiUrl(path);
}

function getAuthHeaders(serviceCode) {
  const tenantCode = sessionDB.getTenantCode();
  const accessToken = sessionDB.getAccessToken();
  return {
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    "X-Tenant-Code": tenantCode,
    "X-Service-Code": serviceCode,
    "tenant-code": tenantCode,
    "service-code": serviceCode,
    Accept: "application/json",
  };
}

function requireAccessToken() {
  if (sessionDB.getAccessToken()) return;

  // Do not send an empty `Authorization: Bearer` header. It results in the
  // misleading backend error shown after a reload when a stale session cannot
  // be restored. Let the app's existing logout listener return the user to
  // the sign-in page instead.
  window.dispatchEvent(new CustomEvent("hrms-logout"));
  const error = new Error("Your session is unavailable. Please sign in again.");
  error.status = 401;
  throw error;
}

function getHeaders(serviceCode) {
  return {
    "Content-Type": "application/json",
    ...getAuthHeaders(serviceCode),
  };
}

async function fetchWithAuthRefresh(requestFactory) {
  await authApi.ensureValidAccessToken();
  const requestToken = sessionDB.getAccessToken();
  let response = await requestFactory();
  const session = sessionDB.getSession();
  if (response.status === 401 && session.rememberMe === true && session.refreshToken) {
    if (session.accessToken === requestToken) await authApi.refreshAccessToken();
    response = await requestFactory();
  }
  return response;
}

async function parseResponse(response) {
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { message: text };
    }
  }

  if (!response.ok) {
    const decodedMessage = getMessage(text);
    const error = new Error(
     decodedMessage || data.message || data.data || `${response.status} ${response.statusText}`
    );
    error.errorCode = data.errorCode;
    error.data = data.data;
    error.status = response.status;
    throw error;
  }
  return data;
}

async function request(method, baseUrl, path, serviceCode, body) {
  try {
    requireAccessToken();
    const response = await fetchWithAuthRefresh(() =>
      fetch(`${baseUrl}${path}`, {
        method,
        headers: getHeaders(serviceCode),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      })
    );
    return await parseResponse(response);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/** Multipart POST — do not set Content-Type; browser adds the FormData boundary. */
async function uploadRequest(baseUrl, path, formData, serviceCode) {
  try {
    requireAccessToken();
    const response = await fetchWithAuthRefresh(() =>
      fetch(`${baseUrl}${path}`, {
        method: "POST",
        headers: getAuthHeaders(serviceCode),
        body: formData,
      })
    );
    return await parseResponse(response);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function parseBlobResponse(response) {
  if (!response.ok) {
    const text = await response.text();
    let data = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }
    }

    const error = new Error(
      data.message || data.data || `${response.status} ${response.statusText}`
    );
    error.errorCode = data.errorCode;
    error.data = data.data;
    error.status = response.status;
    throw error;
  }

  const blob = await response.blob();
  return {
    blob,
    contentDisposition: response.headers.get("Content-Disposition"),
  };
}

function getBlobHeaders(serviceCode, withJsonBody = false) {
  return {
    ...getAuthHeaders(serviceCode),
    ...(withJsonBody ? { "Content-Type": "application/json" } : {}),
    Accept:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*",
  };
}

async function requestBlob(method, baseUrl, path, serviceCode, body) {
  try {
    requireAccessToken();
    const response = await fetchWithAuthRefresh(() =>
      fetch(`${baseUrl}${path}`, {
        method,
        headers: getBlobHeaders(serviceCode, body !== undefined),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      })
    );
    return await parseBlobResponse(response);
  } catch (error) {
    console.error(error);
    throw error;
  }
}
export function hrmsGet(path, serviceCode = "HR01S") {
  return request("GET", HRMS_BASE_URL, path, serviceCode);
}

export function hrmsPost(path, body, serviceCode = "HR01A") {
  return request("POST", HRMS_BASE_URL, path, serviceCode, body);
}

export function hrmsUpload(path, formData, serviceCode = "HR01A") {
  return uploadRequest(HRMS_BASE_URL, path, formData, serviceCode);
}

export function hrmsPut(path, body, serviceCode = "HR01U") {
  return request("PUT", HRMS_BASE_URL, path, serviceCode, body);
}

export function hrmsPatch(path, body, serviceCode="HR01U") {
  return request("PATCH", HRMS_BASE_URL, path, serviceCode, body);
}

export function hrmsDel(path, body, serviceCode="HR01D") {
  return request("DELETE", HRMS_BASE_URL, path, serviceCode, body);
}

export function essGet(path, serviceCode = "ESS03S") {
  return request("GET", ESS_BASE_URL, path, serviceCode);
}

export function essPost(path, body, serviceCode = "ESS03A") {
  return request("POST", ESS_BASE_URL, path, serviceCode, body);
}

export function essPut(path, body, serviceCode = "ESS03U") {
  return request("PUT", ESS_BASE_URL, path, serviceCode, body);
}

export function essPatch(path, body, serviceCode = "ESS03U") {
  return request("PATCH", ESS_BASE_URL, path, serviceCode, body);
}

// Notification records are exposed at the gateway root rather than below the
// ESS base path used by the notification state endpoints.
//export function notificationGet(path, serviceCode = "ESS03S") {
//  return request("GET", NORMALIZED_API_BASE_URL, path, serviceCode);
//}

export function essDel(path, serviceCode = "ESS03D") {
  return request("DELETE", ESS_BASE_URL, path, serviceCode);
}

export function essGetBlob(path, serviceCode = "ESS03S") {
  return requestBlob("GET", ESS_BASE_URL, path, serviceCode);
}

export function essPostBlob(path, body, serviceCode = "ESS03S") {
  return requestBlob("POST", ESS_BASE_URL, path, serviceCode, body);
}

export function psmPost(path, body, serviceCode = "PSM01A") {
  return request("POST", PSM_BASE_URL, path, serviceCode, body);
}

export function psmPut(path, body, serviceCode = "PSM01U") {
  return request("PUT", PSM_BASE_URL, path, serviceCode, body);
}

export function psmPatch(path, body, serviceCode = "PSM01U") {
  return request("PATCH", PSM_BASE_URL, path, serviceCode, body);
}

export function psmDel(path, body, serviceCode = "PSM01D") {
  return request("DELETE", PSM_BASE_URL, path, serviceCode, body);
}

export function psmGet(path, serviceCode = "PSM01S") {
  return request("GET", PSM_BASE_URL, path, serviceCode);
}

export function crmGet(path, serviceCode = "CRM01S") {
  return request("GET", CRM_BASE_URL, path, serviceCode);
}

export function crmPost(path, body, serviceCode = "CRM01A") {
  return request("POST", CRM_BASE_URL, path, serviceCode, body);
}

export function crmPut(path, body, serviceCode = "CRM01U") {
  return request("PUT", CRM_BASE_URL, path, serviceCode, body);
}

export function crmPatch(path, body, serviceCode = "CRM01U") {
  return request("PATCH", CRM_BASE_URL, path, serviceCode, body);
}

export function crmDel(path, serviceCode = "CRM01D") {
  return request("DELETE", CRM_BASE_URL, path, serviceCode);
}

export function fsmGet(path, serviceCode = "FSM01S") {
  return request("GET", FSM_BASE_URL, path, serviceCode);
}

export function fsmPost(path, body, serviceCode = "FSM01A") {
  return request("POST", FSM_BASE_URL, path, serviceCode, body);
}

export function fsmPut(path, body, serviceCode = "FSM01U") {
  return request("PUT", FSM_BASE_URL, path, serviceCode, body);
}

export function fsmDel(path, serviceCode = "FSM01D") {
  return request("DELETE", FSM_BASE_URL, path, serviceCode);
}

export function imsGet(path, serviceCode = "IMS04S") {
  return request("GET", IMS_BASE_URL, path, serviceCode);
}

export function imsPost(path, body, serviceCode = "IMS04A") {
  return request("POST", IMS_BASE_URL, path, serviceCode, body);
}

export function imsPut(path, body, serviceCode = "IMS04U") {
  return request("PUT", IMS_BASE_URL, path, serviceCode, body);
}

export function imsDel(path, serviceCode = "IMS04D") {
  return request("DELETE", IMS_BASE_URL, path, serviceCode);
}

 export function imsPatch(path, body, serviceCode = "IMS04U") {
  return request("PATCH", IMS_BASE_URL, path, serviceCode, body);
}